import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { STALE_TIME_30_SECONDS, STALE_TIME_1_MINUTE, STALE_TIME_5_MINUTES, STALE_TIME_1_HOUR, STALE_TIME_24_HOURS } from "@/lib/query-constants";

/**
 * SEARCH-010: Recent & Saved Searches
 * 
 * Persist recent searches for quick reuse.
 * 
 * Rules:
 * - User-specific
 * - No auto-execution
 * - No sharing in V1
 */

interface SearchHistoryEntry {
  id: string;
  query: string;
  objectTypes: string[] | null;
  resultCount: number;
  searchedAt: string;
}

interface SearchHistoryRow {
  id: string;
  query: string;
  object_types: string[] | null;
  result_count: number | null;
  searched_at: string;
}

export function useSearchHistory() {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { user } = useAuth();
  
  // Fetch from database
  const { data: dbHistory } = useQuery({
    queryKey: ["search-history", organization?.id, user?.id],
    queryFn: async (): Promise<SearchHistoryEntry[]> => {
      if (!organization?.id || !user?.id) return [];
      
      // Use rpc or raw query since types may not be regenerated yet
      const { data, error } = await supabase
        .from("search_history")
        .select("id, query, object_types, result_count, searched_at")
        .eq("organization_id", organization.id)
        .eq("user_id", user.id)
        .order("searched_at", { ascending: false })
        .limit(20);
      
      if (error) {
        console.error("Error fetching search history:", error);
        return [];
      }
      
      return ((data || []) as unknown as SearchHistoryRow[]).map(entry => ({
        id: entry.id,
        query: entry.query,
        objectTypes: entry.object_types,
        resultCount: entry.result_count ?? 0,
        searchedAt: entry.searched_at,
      }));
    },
    enabled: !!organization?.id && !!user?.id,
    staleTime: STALE_TIME_1_MINUTE,
  });
  
  // Local state for immediate updates
  const [localHistory, setLocalHistory] = useState<SearchHistoryEntry[]>([]);
  
  // Merge database and local history
  const recentSearches = useMemo(() => {
    const merged = [...localHistory];
    
    if (dbHistory) {
      for (const entry of dbHistory) {
        if (!merged.some(e => e.query === entry.query)) {
          merged.push(entry);
        }
      }
    }
    
    // Deduplicate by query and sort by date
    const seen = new Set<string>();
    return merged
      .filter(entry => {
        if (seen.has(entry.query.toLowerCase())) return false;
        seen.add(entry.query.toLowerCase());
        return true;
      })
      .sort((a, b) => new Date(b.searchedAt).getTime() - new Date(a.searchedAt).getTime())
      .slice(0, 10);
  }, [dbHistory, localHistory]);
  
  // Add to history mutation
  const addMutation = useMutation({
    mutationFn: async ({ query, resultCount, objectTypes }: { 
      query: string; 
      resultCount: number;
      objectTypes?: string[];
    }) => {
      if (!organization?.id || !user?.id) return null;
      
      const { data, error } = await supabase
        .from("search_history")
        .insert({
          query: query.trim(),
          result_count: resultCount,
          object_types: objectTypes || null,
          organization_id: organization.id,
          user_id: user.id,
        })
        .select("id")
        .single();
      
      if (error) {
        console.error("Error saving search history:", error);
        return null;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-history"] });
    },
  });
  
  // Add to history (debounced)
  const addToHistory = useCallback(
    (query: string, resultCount: number, objectTypes?: string[]) => {
      if (!query.trim() || query.length < 2) return;
      
      // Add to local state immediately
      const newEntry: SearchHistoryEntry = {
        id: crypto.randomUUID(),
        query: query.trim(),
        objectTypes: objectTypes || null,
        resultCount,
        searchedAt: new Date().toISOString(),
      };
      
      setLocalHistory(prev => {
        const filtered = prev.filter(e => e.query.toLowerCase() !== query.toLowerCase());
        return [newEntry, ...filtered].slice(0, 10);
      });
      
      // Persist to database
      addMutation.mutate({ query, resultCount, objectTypes });
    },
    [addMutation]
  );
  
  // Clear history mutation
  const clearMutation = useMutation({
    mutationFn: async () => {
      if (!organization?.id || !user?.id) return;
      
      const { error } = await supabase
        .from("search_history")
        .delete()
        .eq("organization_id", organization.id)
        .eq("user_id", user.id);
      
      if (error) {
        console.error("Error clearing search history:", error);
      }
    },
    onSuccess: () => {
      setLocalHistory([]);
      queryClient.invalidateQueries({ queryKey: ["search-history"] });
    },
  });
  
  const clearHistory = useCallback(() => {
    clearMutation.mutate();
  }, [clearMutation]);
  
  // Add removeFromHistory function
  const removeFromHistory = useCallback(
    async (query: string) => {
      if (!organization?.id || !user?.id) return;
      
      // Remove from local state
      setLocalHistory(prev => prev.filter(e => e.query.toLowerCase() !== query.toLowerCase()));
      
      // Remove from database
      try {
        await supabase
          .from("search_history")
          .delete()
          .eq("organization_id", organization.id)
          .eq("user_id", user.id)
          .eq("query", query);
        
        queryClient.invalidateQueries({ queryKey: ["search-history"] });
      } catch (error) {
        console.error("Error removing search history:", error);
      }
    },
    [organization?.id, user?.id, queryClient]
  );

  return {
    recentSearches,
    addToHistory,
    removeFromHistory,
    clearHistory,
    isLoading: addMutation.isPending,
  };
}
